import { useIonViewDidLeave } from "@ionic/react";
import React, { FC, useContext, useEffect, useState, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";

import { Context, timelinePath, careerQuestionPath } from "../../store/student";
import PieChart from "../../atoms/Career/PieChart";

import styles from "./CareerQuestion.module.scss";

type Props = {
  createRealization: (questionId: number, answer: string) => void;
};

const CareerQuestion: FC<Props> = ({ createRealization }) => {
  const { contextState } = useContext(Context);
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const paramsQuestionId = Number(params.id);
  const isMountedRef = useRef(true);

  const [answer, setAnswer] = useState<string>("");

  const feedbackQuestions = contextState.career_feedback_questions;
  const currentQuestionIndex =
    paramsQuestionId > 0 && paramsQuestionId <= feedbackQuestions.length
      ? paramsQuestionId - 1
      : -1;
  const currentQuestion =
    currentQuestionIndex !== -1
      ? feedbackQuestions[currentQuestionIndex]
      : null;
  const currentQuestionNo = currentQuestionIndex + 1;

  useEffect(() => {
    if (!isMountedRef.current || feedbackQuestions.length === 0) return;

    const unansweredQuestionIndex = feedbackQuestions.findIndex(
      question => question.realization === null,
    );

    if (unansweredQuestionIndex === -1) {
      history.push(timelinePath);
      return;
    }

    if (currentQuestionIndex === -1 && params.id) {
      history.push(`${careerQuestionPath}/${unansweredQuestionIndex + 1}`);
    }
  }, [feedbackQuestions, currentQuestionIndex, history, params.id]);

  useEffect(() => {
    if (currentQuestion?.realization?.content) {
      setAnswer(currentQuestion.realization.content);
    } else {
      setAnswer("");
    }
  }, [currentQuestion]);

  // ページ離脱後にリダイレクト処理が実行されないようクリーンアップ
  useIonViewDidLeave(() => {
    isMountedRef.current = false;
  });

  const goToNextPage = () => {
    if (currentQuestionNo === feedbackQuestions?.length) {
      history.push(timelinePath);
    } else {
      history.push(`${careerQuestionPath}/${currentQuestionNo + 1}`);
    }
  };

  // Titleが長く読みづらいくなるため改行を必ずいれることとする
  const splitTitle = (title: string) => {
    const breakPoints = ["?", "？", "。"];
    const breakIndex = breakPoints
      .map(char => title.indexOf(char))
      .filter(index => index !== -1)
      .sort((a, b) => a - b)[0];

    if (breakIndex === undefined) {
      return { firstLine: title, secondLine: "" };
    }

    return {
      firstLine: title.slice(0, breakIndex + 1),
      secondLine: title.slice(breakIndex + 1),
    };
  };
  const { firstLine, secondLine } = splitTitle(currentQuestion?.title ?? "");

  return (
    <div className={styles.wrapper}>
      <PieChart
        count={currentQuestionIndex}
        length={feedbackQuestions?.length}
      />
      <p className={styles.theme}>Q.キャリア開拓クエスチョン</p>
      <p className={styles.title}>
        {firstLine}
        {secondLine && <br />}
        {secondLine}
      </p>
      <textarea
        className={styles.textarea}
        value={answer}
        onChange={e => setAnswer(e.target.value)}
        placeholder="自由な発想で書いてみよう。"
      />
      <div className={styles.footer}>
        <button
          className={styles.stock}
          onClick={() => {
            goToNextPage();
            if (currentQuestion?.id) {
              createRealization(currentQuestion.id, answer);
            }
          }}
          disabled={answer === ""}
        >
          ストックする
        </button>
      </div>
    </div>
  );
};

export default CareerQuestion;
